import { useState } from "react";
import { AlertTriangle, Cpu, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { MAC_APPS, searchMac } from "@/lib/macLookup";
import { MacMatch } from "@/components/clients/MacLookupBox";

const fmt = (v) => (v || "").replace(/[^0-9a-fA-F]/g, "").toUpperCase().slice(0, 12).match(/.{1,2}/g)?.join(":") || "";
const label = (k) => (MAC_APPS.find(([a]) => a === k) || [null, "app não identificado"])[1];

const MacRead = ({ s, r, onLinked, openClient }) => {
  const [mac, setMac] = useState(r.mac);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (value = mac) => {
    setBusy(true);
    try {
      const single = r.apps?.length === 1 ? r.apps[0] : null;
      const resp = await searchMac(value, single);
      let hits = resp.matches;
      if (r.apps?.length) hits = hits.filter((m) => r.apps.includes(m.app));
      setRes({ hits, errors: Object.entries(resp.errors || {}).map(([app, error]) => ({ app, error })) });
      const one = hits.filter((m) => m.clients.length === 1);
      if (!s.client_id && one.length && one.every((m) => m.clients[0].id === one[0].clients[0].id)) {
        await api.post(`/chat/sessions/${s.id}/link`, { client_id: one[0].clients[0].id });
        onLinked();
      }
    } catch (e) {
      setRes({ error: e.response?.data?.detail || "Falha ao buscar nos painéis" });
    }
    setBusy(false);
  };

  return (
    <div data-testid={`chat-mac-read-${r.msg_id}`} className="rounded-lg border border-pink-500/30 bg-pink-500/[0.06] px-3 py-2 text-xs">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Cpu className="h-3.5 w-3.5 text-pink-300" />
        <span className="font-semibold text-pink-200">MAC</span>
        <span className="ml-auto truncate text-[11px] text-slate-400">{r.app_name || "app não identificado"}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <input data-testid="chat-mac-read-input" value={mac} onChange={(e) => setMac(fmt(e.target.value))}
          className="min-w-0 flex-1 rounded-md border border-[#1F293D] bg-[#0B1019] px-2 py-1 font-mono text-[13px] text-white focus:border-pink-500 focus:outline-none" />
        <button data-testid="chat-mac-read-search" disabled={busy || mac.length !== 17} onClick={() => run()} title="Buscar nos painéis"
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-pink-500 px-2 py-1 font-bold text-slate-950 transition-opacity hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Buscar
        </button>
      </div>
      {res && (
        <div data-testid="chat-mac-read-results" className="mt-2 space-y-1">
          {res.error && <div className="text-amber-300">{res.error}</div>}
          {res.hits?.map((m, i) => <MacMatch key={i} app={m.app} m={m} onOpen={openClient} />)}
          {res.hits && !res.hits.length && <div className="text-slate-400">MAC não encontrado nos painéis. Confira os caracteres (ex.: B e 8, D e 0) e busque de novo.</div>}
          {res.errors?.map((o) => <div key={o.app} className="text-[11px] text-red-300">{label(o.app)}: {o.error}</div>)}
        </div>
      )}
    </div>
  );
};

export const MacReads = ({ s, onLinked, openClient }) => {
  const reads = [...new Map((s.mac_reads || []).filter((r) => r.mac).map((r) => [r.mac, r])).values()].reverse();
  if (!reads.length && !s.mac_alert) return null;
  return (
    <div data-testid="chat-mac-reads" className="space-y-2">
      {s.mac_alert && (
        <div data-testid="chat-mac-alert" className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200">
          <AlertTriangle className="h-4 w-4" /> Bia: {s.mac_alert}
        </div>
      )}
      {reads.map((r) => <MacRead key={r.mac} s={s} r={r} onLinked={onLinked} openClient={openClient} />)}
    </div>
  );
};
